// The neighbor timeline renders its first page and loads the rest through a
// lazy `interactions_page_next` frame as the reader scrolls. A link or jump to
// a row on a later page loads the pages before it instead of waiting.

type Loader = HTMLElement & { loaded?: Promise<void> }

const pending = new Map<string, Promise<HTMLElement | null>>()

// Links and jumps name rows `interaction_12_row`. Incident rows keep the bare
// `interaction_12` (incidents/destroy removes them by it).
export function findRow(id: string): HTMLElement | null {
  return document.getElementById(id) ?? document.getElementById(id.replace(/_row$/, ""))
}

// Resolves with the row once its page has loaded, or null when the list ends
// without it. Concurrent calls for one id share the same paging.
export function revealRow(id: string): Promise<HTMLElement | null> {
  let reveal = pending.get(id)
  if (!reveal) {
    reveal = pageUntil(id).finally(() => pending.delete(id))
    pending.set(id, reveal)
  }
  return reveal
}

async function pageUntil(id: string): Promise<HTMLElement | null> {
  let loader: Loader | null = null
  for (;;) {
    const row = findRow(id)
    if (row) return row

    const next = document.getElementById("interactions_page_next") as Loader | null
    // The same loader again means its page failed to load or unwrap.
    if (!next || next === loader) return null

    loader = next
    // A busy loader is already fetching; restarting it would cancel that.
    if (!loader.hasAttribute("busy")) loader.setAttribute("loading", "eager")
    await loader.loaded
  }
}
